import { useEffect, useState } from "react";
import { Download, Share, PlusSquare, X } from "lucide-react";

const setTag = (sel, tag, attrs) => {
  let el = document.head.querySelector(sel);
  if (!el) { el = document.createElement(tag); document.head.appendChild(el); }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
};

export const usePortalPwa = (code, name) => {
  useEffect(() => {
    if (!name) { return; }
    setTag('link[rel="manifest"]', "link", { rel: "manifest", href: `${process.env.REACT_APP_BACKEND_URL}/api/portal/${code}/manifest.webmanifest` });
    setTag('link[rel="apple-touch-icon"]', "link", { rel: "apple-touch-icon", href: "/pwa-180.png" });
    setTag('meta[name="theme-color"]', "meta", { name: "theme-color", content: "#064E3B" });
    setTag('meta[name="apple-mobile-web-app-capable"]', "meta", { name: "apple-mobile-web-app-capable", content: "yes" });
    setTag('meta[name="mobile-web-app-capable"]', "meta", { name: "mobile-web-app-capable", content: "yes" });
    setTag('meta[name="apple-mobile-web-app-status-bar-style"]', "meta", { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" });
    setTag('meta[name="apple-mobile-web-app-title"]', "meta", { name: "apple-mobile-web-app-title", content: name.slice(0, 12) });
    document.title = `${name} – VAT Portal`;
    if ("serviceWorker" in navigator) { navigator.serviceWorker.register("/portal-sw.js", { scope: "/portal/" }).catch(() => {}); }
  }, [code, name]);
};

const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

export const InstallApp = ({ dark }) => {
  const [evt, setEvt] = useState(null);
  const [help, setHelp] = useState(false);
  const [done, setDone] = useState(isStandalone());
  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setEvt(e); };
    const onInstalled = () => setDone(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener("appinstalled", onInstalled); };
  }, []);
  if (done) return null;
  const click = async () => {
    if (evt) { evt.prompt(); const r = await evt.userChoice; if (r.outcome === "accepted") { setDone(true); } setEvt(null); } else { setHelp(true); }
  };
  return (
    <>
      <button onClick={click} data-testid="portal-install-btn"
        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${dark ? "bg-white/10 text-white active:bg-white/20" : "bg-amber-500 text-[#064E3B] active:bg-amber-400"}`}>
        <Download className="w-3.5 h-3.5" />Add to home screen
      </button>
      {help && <InstallHelp ios={isIos()} onClose={() => setHelp(false)} />}
    </>
  );
};

const Step = ({ n, children }) => (
  <li className="flex gap-3 items-start"><span className="w-6 h-6 shrink-0 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold flex items-center justify-center">{n}</span><span className="pt-0.5">{children}</span></li>
);

const InstallHelp = ({ ios, onClose }) => (
  <div className="fixed inset-0 z-50 bg-black/50 flex items-end" onClick={onClose} data-testid="portal-install-help">
    <div className="w-full bg-white text-slate-800 rounded-t-3xl p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))]" onClick={(e) => e.stopPropagation()}>
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-semibold text-lg">Add to home screen</h3>
        <button onClick={onClose} className="p-1 rounded-full active:bg-slate-100" data-testid="portal-install-help-close"><X className="w-5 h-5" /></button>
      </div>
      <ol className="space-y-3 text-sm">
        {ios ? (
          <>
            <Step n={1}>Open this link in <b>Safari</b>.</Step>
            <Step n={2}>Tap the <Share className="inline w-4 h-4 -mt-0.5" /> <b>Share</b> button at the bottom.</Step>
            <Step n={3}>Choose <PlusSquare className="inline w-4 h-4 -mt-0.5" /> <b>Add to Home Screen</b>, then tap <b>Add</b>.</Step>
          </>
        ) : (
          <>
            <Step n={1}>Open this link in <b>Chrome</b>.</Step>
            <Step n={2}>Tap the <b>⋮</b> menu at the top right.</Step>
            <Step n={3}>Choose <b>Add to Home screen</b> or <b>Install app</b>.</Step>
          </>
        )}
      </ol>
      <p className="text-xs text-slate-500 mt-4">The portal will then open full-screen from its own icon, like an app.</p>
    </div>
  </div>
);
